// Monitor — live progress for an in-flight Run.
//
// Rewrite goals:
//   * One source of truth (polling). No fake math, no oscillation.
//   * Counts/labels driven by the run's own config (not the user's local
//     configStore, which can lag if you reload the page or come from a
//     deep link).
//   * Drop the unsupported affordances: Slack, Open org (no useful URL
//     mid-run), ETA (worker pct is not linear so estimates lie), and
//     the Dashboards counter (no backend signal yet).

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { EtaChip } from "@/components/monitor/EtaChip";
import { PACKS, PHASES, PROFILES_LIST, VOLUMES } from "@/lib/data";
import { useUiStore } from "@/lib/store/uiStore";
import type { RunPhase } from "@/lib/types";
import type { RawJobShape } from "@/lib/api/real";

const STAGE_GROUPS: {
  id: "pack" | "scope" | "stories" | "pipeline";
  label: string;
  phases: number[];
}[] = [
  { id: "pack",     label: "Company profile",  phases: [0, 1] },
  { id: "scope",    label: "Scope & volume",   phases: [2] },
  { id: "stories",  label: "Stories to land",  phases: [3, 4] },
  { id: "pipeline", label: "Pipeline targets", phases: [5, 6, 7, 8] },
];

interface JobStepLog { step: string; status?: string; pct?: number; log?: string }

export function Monitor(): JSX.Element {
  const { runId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showToast = useUiStore((s) => s.showToast);
  const [livePhases, setLivePhases] = useState<RunPhase[]>([]);
  // Monotonic high-water mark — the worker's _progress(pct) calls are
  // not strictly monotonic at phase boundaries (cleanup pct=10 → phase 2
  // setup pct=15 → phase 2 step pct=12 from a cached snapshot), so we
  // clamp to the highest pct we've ever seen for this run.
  const highWaterRef = useRef(0);
  const [highWater, setHighWater] = useState(0);

  // SINGLE shared query for both the mapped Run and the raw job payload.
  // Two queries hammering /api/jobs/:id at 2s intervals (`run` + `run-raw`)
  // used to blow the 60/min rate limit on long-running phases — the 429s
  // would silently starve the EtaChip pace fetch and freeze the heartbeat.
  // The realApi method returns both shapes from a single fetch, so React
  // Query only schedules one poll cycle.
  //
  // C3 (2026-04-20): The query key MUST differ from the `["run", runId]`
  // key used by RunDetail / Done. Those pages cache a plain `Run`; this
  // page caches `{ run, raw }`. With identical keys, navigating
  // RunDetail → Monitor (or vice-versa) would surface the cached value
  // with the wrong shape and crash on `combined.run` / `run.phases`.
  const { data: combined } = useQuery({
    queryKey: ["run-with-raw", runId],
    queryFn: () => api.getRunWithRaw(runId),
    refetchInterval: 2000,
    refetchIntervalInBackground: false,
  });
  const run = combined?.run;
  const rawJob = combined?.raw as
    | (RawJobShape & { steps_log?: JobStepLog[] })
    | null
    | undefined;

  // Pace + ETA — separate poll so a pace endpoint hiccup doesn't block
  // the main run-status query. 5s cadence is plenty for a chip that
  // shows minute-granularity ETAs.
  const { data: pace } = useQuery({
    queryKey: ["run-pace", runId],
    queryFn: () => api.getJobPace(runId),
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
    enabled: !!runId && rawJob?.status === "running",
  });

  // C4 (2026-04-20): Drive `livePhases` straight off the React Query
  // poll above. The previous implementation also fired `subscribeToRun`
  // which internally calls `getRun(runId)` every 2s — that doubled the
  // /api/jobs/:id request rate and the `useEffect` deps included
  // `run?.phases` (a new array reference every poll), so the
  // subscription was unmounted and re-mounted every cycle. Removing
  // it halves the per-Monitor-tab request count and eliminates the
  // duplicated work without losing live updates: the 2s `useQuery`
  // already refreshes `combined.run.phases`, and the terminal-status
  // navigation now happens in a tiny effect that only runs when
  // `run.status` flips.
  useEffect(() => {
    if (run?.phases) {
      setLivePhases(run.phases);
    }
  }, [run?.phases]);

  // PROD-A3 (2026-04-21): only auto-redirect to /done on a status
  // *transition* (running/queued → terminal). Without the
  // ``prevStatusRef`` guard, a user who clicked "View deployment
  // errors" on the partial-success Done page would land on
  // /monitor/:id, which would immediately see ``status ===
  // "complete_with_errors"`` and bounce them right back to /done —
  // an infinite loop. ``failed`` and ``cancelled`` get the same
  // treatment so any "back to monitor" deep link from a notification
  // also stays put.
  const prevStatusRef = useRef<string | null>(null);
  useEffect(() => {
    const status = run?.status ?? null;
    const wasNonTerminal =
      prevStatusRef.current === "running" ||
      prevStatusRef.current === "queued";
    const isNowTerminal =
      status === "complete" ||
      status === "complete_with_errors" ||
      status === "failed" ||
      status === "cancelled";
    if (wasNonTerminal && isNowTerminal) {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      void queryClient.invalidateQueries({ queryKey: ["runs"] });
      navigate(`/done/${runId}`);
    }
    prevStatusRef.current = status;
  }, [run?.status, runId, navigate, queryClient]);

  const phases = livePhases.length > 0 ? livePhases : run?.phases ?? [];
  const observedPct = useMemo(() => {
    if (phases.length === 0) return 0;
    return Math.round(
      phases.reduce((sum, p) => sum + (p.pct || 0), 0) / phases.length,
    );
  }, [phases]);

  // Bump high-water mark on every observation; never let displayed pct
  // decrease.
  useEffect(() => {
    if (observedPct > highWaterRef.current) {
      highWaterRef.current = observedPct;
      setHighWater(observedPct);
    }
  }, [observedPct]);
  const overallPct = Math.max(observedPct, highWater);

  const phaseIdx = Math.max(
    0,
    phases.findIndex((p) => p.status === "running"),
  );
  const currentPhase = phases[phaseIdx] ?? phases[0];

  // Source volume from the run config (not configStore — that may not
  // match if the user lands here via deep link or browser refresh).
  // Prefer explicit ``num_accounts`` / ``num_opportunities`` /
  // ``num_cases`` if the backend includes them in the resolved config
  // (so override-driven runs surface their actual targets), and fall
  // back to the ``volume_preset``-derived defaults from VOLUMES.
  const volPreset = (rawJob?.config?.volume_preset as string | undefined)
    ?? (run?.volume ?? "").toLowerCase();
  const vol = VOLUMES.find((v) => v.id === volPreset);
  const cfg = rawJob?.config ?? {};
  const cfgNum = (key: string): number | null => {
    const v = cfg[key];
    return typeof v === "number" && Number.isFinite(v) ? v : null;
  };
  const accountsTarget = cfgNum("num_accounts") ?? vol?.accounts ?? null;
  const oppsTarget = cfgNum("num_opportunities") ?? vol?.opps ?? null;
  const casesTarget = cfgNum("num_cases") ?? vol?.cases ?? null;
  const selectedStories = (cfg.selected_stories as string[] | undefined) ?? run?.stories ?? [];
  const storiesTotal = selectedStories.length || (run?.storiesCount ?? 0);

  // Resolve the pack's logo by name (or industry_pack_id when present),
  // not the hard-coded Cirrus asset that used to ship with every run.
  // Fallback chain: built-in/profile logo → community-pack logo →
  // backend resolver (/api/packs/:id/logo, returns uploaded logo or
  // monogram SVG) → bundled Tableau wordmark for the hero. The hero
  // gets the Tableau fallback because it's the most-watched surface
  // during a demo and a generic monogram looks half-loaded; grids
  // intentionally keep the monogram (see PackLogo `fallback` prop).
  const packId = (cfg.industry_pack_id as string | undefined) ?? "";
  const packLogo =
    PROFILES_LIST.find((p) => p.id === packId)?.logo ??
    PACKS.find((p) => p.name === run?.profile)?.logo ??
    (packId ? `/api/packs/${encodeURIComponent(packId)}/logo` : null);
  const tableauFallback = "/assets/tableau-logo.png";

  const stageStatuses = STAGE_GROUPS.map((sg) => {
    const max = Math.max(...sg.phases);
    const min = Math.min(...sg.phases);
    // PROD-A3 (2026-04-21): partial-success counts as terminal-done
    // for the stage strip — the worker walked every phase, even if
    // an analytics sub-step inside Phase 6 silently failed. The amber
    // surface lives in the DeployErrorsPanel and the EtaChip below,
    // not in the strip itself (turning a phase amber when its
    // children are spread across multiple status keys is messy).
    if (run?.status === "complete" || run?.status === "complete_with_errors")
      return "done" as const;
    if (phaseIdx > max) return "done" as const;
    if (phaseIdx >= min) return "running" as const;
    return "queued" as const;
  });

  const fallbackPhase = currentPhase
    ? { label: currentPhase.label, steps: [] as string[] }
    : PHASES[Math.min(phaseIdx, PHASES.length - 1)];

  const phaseLabel = (currentPhase?.label ?? fallbackPhase.label).replace(
    /^Phase \d+[a-z]?[: ·] ?/i,
    "",
  );

  const liveStepText = (rawJob?.current_step ?? "").trim();
  const stepsLog = rawJob?.steps_log ?? [];

  return (
    <div
      style={{ maxWidth: 1080, margin: "0 auto", padding: "28px 24px 120px" }}
    >
      {/* ─── Hero ─── */}
      <div
        style={{
          position: "relative",
          padding: 22,
          borderRadius: 16,
          background:
            "linear-gradient(135deg, var(--eb-15) 0%, var(--eb-30) 55%, #1558a0 100%)",
          color: "white",
          overflow: "hidden",
          boxShadow: "0 8px 32px -12px rgba(3,45,96,.35)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 24,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 10,
                flexWrap: "wrap",
              }}
            >
              <div className="label" style={{ color: "#AACBE2" }}>
                Flight in progress
              </div>
              <span
                style={{
                  fontSize: 10,
                  padding: "2px 9px",
                  borderRadius: 999,
                  background: "rgba(75,192,118,.25)",
                  color: "#A8E5BD",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <span
                  className="pulse-dot"
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: "#4BC076",
                  }}
                />
                Live · {phaseLabel}
              </span>
              {run?.cloud && run.cloud !== "—" && (
                <span
                  style={{
                    fontSize: 10,
                    padding: "2px 9px",
                    borderRadius: 999,
                    background: "rgba(255,255,255,.12)",
                    color: "#C9E3F6",
                  }}
                >
                  {run.cloud}
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              {/* Solid white pill so dark/transparent pack logos read on
                  the dark hero gradient.  The previous translucent-white
                  treatment made any logo with dark ink (e.g. the official
                  Cirrus mark) effectively invisible. */}
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid rgba(255,255,255,.4)",
                  boxShadow: "0 2px 6px rgba(0,0,0,.12)",
                  flexShrink: 0,
                }}
              >
                <img
                  src={packLogo ?? tableauFallback}
                  alt=""
                  style={{ height: 30, width: 30, objectFit: "contain" }}
                  onError={(e) => {
                    const img = e.currentTarget;
                    if (img.src !== window.location.origin + tableauFallback) {
                      img.src = tableauFallback;
                    }
                  }}
                />
                
              </div>
              <div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {run?.profile ?? "Custom profile"}
                </div>
                <div style={{ fontSize: 12, color: "#AACBE2" }}>
                  {vol?.label ?? run?.volume ?? "—"} volume
                  {storiesTotal ? ` · ${storiesTotal} stories selected` : ""}
                </div>
              </div>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontSize: 36,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                lineHeight: 1,
              }}
            >
              {overallPct}
              <span
                style={{
                  fontSize: 18,
                  color: "#AACBE2",
                  fontWeight: 600,
                }}
              >
                %
              </span>
            </div>
            <EtaChip
              pace={pace ?? null}
              lastUpdatedAt={rawJob?.updated_at ?? null}
              fallbackStatus={
                run?.status === "queued"
                  ? "Waiting for worker…"
                  : run?.status === "running"
                    ? "Provisioning…"
                    : run?.status === "complete"
                      ? "Complete"
                      : run?.status === "complete_with_errors"
                        ? "⚠ Completed with errors"
                        : run?.status === "failed"
                          ? "Failed"
                          : "—"
              }
            />

          </div>
        </div>

        {/* progress */}
        <div
          style={{
            height: 6,
            borderRadius: 999,
            marginTop: 18,
            marginBottom: 16,
            background: "rgba(255,255,255,.12)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${overallPct}%`,
              height: "100%",
              background: "linear-gradient(90deg, #4aa3e3, #6fc4f5)",
              borderRadius: 999,
              transition: "width .4s ease",
              boxShadow: "0 0 12px rgba(111,196,245,.5)",
            }}
          />
        </div>

        {/* Target counts (no fake live math — just show what we're aiming
            at). When we don't have a known volume, hide this strip. */}
        {vol && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 1,
              background: "rgba(255,255,255,.12)",
              borderRadius: 10,
              padding: 1,
            }}
          >
            {[
              ["Accounts target", accountsTarget?.toLocaleString() ?? "—"],
              ["Opportunities target", oppsTarget?.toLocaleString() ?? "—"],
              ["Cases target", casesTarget?.toLocaleString() ?? "—"],
              ["Stories selected", storiesTotal || "—"],
            ].map(([k, v], i) => (
              <div
                key={k as string}
                style={{
                  padding: "10px 14px",
                  background:
                    "linear-gradient(180deg, rgba(3,45,96,.55) 0%, rgba(3,45,96,.7) 100%)",
                  borderRadius:
                    i === 0
                      ? "9px 0 0 9px"
                      : i === 3
                        ? "0 9px 9px 0"
                        : 0,
                }}
              >
                <div className="label" style={{ color: "#8BA4BE" }}>
                  {k}
                </div>
                <div style={{ fontSize: 17, fontWeight: 700, marginTop: 2 }}>
                  {v}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stale / failure banner */}
      {rawJob?.is_stale && rawJob?.stale_reason && (
        <div
          className="card"
          style={{
            marginTop: 16,
            padding: "12px 16px",
            borderColor: "var(--orange)",
            background: "rgba(243,154,55,.08)",
            color: "var(--ink)",
            fontSize: 13,
          }}
        >
          ⚠️ {rawJob.stale_reason}
        </div>
      )}

      {/* Phase 6 partial-failure banner — shown when the run finished
          but the analytics layer (Data Cloud / Tableau Next / sample
          dashboards) left one or more persisted error markers in
          phase6_outputs. Mirrors the backend's "Complete (partial)"
          demotion at pipeline_runner.py so the SE sees the same truth
          the Papertrail log does without opening Slack.
          See docs/CURSOR_SPEC_TC26_PHASE6_SCOPE.md §6.2. */}
      <DeployErrorsPanel rawJob={rawJob} />

      {/* Stage strip */}
      <div
        style={{
          display: "flex",
          margin: "20px 0 0",
          background: "white",
          border: "1px solid var(--bd)",
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        {STAGE_GROUPS.map((sg, i) => {
          const st = stageStatuses[i];
          return (
            <div
              key={sg.id}
              style={{
                flex: 1,
                padding: "14px 16px",
                borderRight:
                  i < STAGE_GROUPS.length - 1
                    ? "1px solid var(--bd-2)"
                    : "none",
                background: st === "running" ? "#eef6fc" : "white",
                position: "relative",
              }}
            >
              {st === "running" && (
                <div
                  className="pulse-dot"
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    // Pacing colour — green/amber/red driven by /api/jobs/:id/pace.
                    // Falls back to the existing brand blue when we have no
                    // baseline yet (first run on this config).
                    background:
                      pace?.pace_status === "behind"
                        ? "#FF6B6B"
                        : pace?.pace_status === "slightly_behind"
                          ? "#F2C94C"
                          : pace?.pace_status === "on_pace"
                            ? "#7CDB8F"
                            : "var(--eb-50)",
                  }}
                />
              )}
              <div
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                    background:
                      st === "done"
                        ? "var(--teal)"
                        : st === "running"
                          ? "var(--eb-50)"
                          : "var(--cb-95)",
                    color: st === "queued" ? "var(--ink-3)" : "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {st === "done" ? "✓" : i + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="label">
                    {st === "done"
                      ? "✓ Complete"
                      : st === "running"
                        ? "Running"
                        : "Queued"}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--ink)",
                      marginTop: 1,
                    }}
                  >
                    {sg.label}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Terminal log */}
      <div
        className="card"
        style={{
          marginTop: 0,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderTop: "none",
          padding: 0,
        }}
      >
        <div
          style={{
            padding: "14px 20px",
            borderBottom: "1px solid var(--bd-2)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--ink)",
              }}
            >
              {currentPhase?.label ?? fallbackPhase.label}
            </div>
            <div
              className="pulse-dot"
              style={{
                fontSize: 11,
                color: "var(--ink-3)",
                marginTop: 2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={liveStepText}
            >
              › {liveStepText || currentPhase?.steps.find((s) => !s.done)?.label || "Working…"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn danger sm"
              onClick={async () => {
                if (!confirm("Cancel this run?")) return;
                // 2026-05-03 (C-MEDIUM): the previous handler swallowed
                // every cancel error and unconditionally navigated to
                // /runs. A 401 (session expired), 404 (race with a
                // worker-side completion), or 500 (RQ unreachable) all
                // looked identical to the user — they thought the run
                // was cancelled when in fact it was still consuming
                // dyno time. Surface the failure via the existing
                // toast channel and stay on the Monitor page so the
                // user can retry. ``invalidateQueries`` keeps the
                // happy path snappy without losing the live polling.
                try {
                  await api.cancelRun(runId);
                  showToast("Run cancelled");
                  void queryClient.invalidateQueries({ queryKey: ["runs"] });
                  navigate("/runs");
                } catch (err) {
                  const msg = err instanceof Error ? err.message : String(err);
                  showToast(`Cancel failed: ${msg}`);
                }
              }}
            >
              Cancel run
            </button>
          </div>
        </div>
        <div
          style={{
            padding: 16,
            background: "var(--cb-98)",
            fontFamily: "ui-monospace, Menlo, monospace",
            fontSize: 11,
            color: "var(--ink-2)",
            lineHeight: 1.7,
            maxHeight: 320,
            overflow: "auto",
          }}
        >
          {stepsLog.length > 0 ? (
            stepsLog.slice(-50).map((entry, i) => (
              <div key={`${entry.step}-${i}`}>
                <span style={{ color: "var(--teal)" }}>
                  {String(entry.pct ?? "").padStart(3, " ")}%
                </span>{" "}
                <span style={{ color: "var(--ink-2)" }}>{entry.step}</span>
                {entry.log && (
                  <span style={{ color: "var(--ink-3)" }}> · {entry.log}</span>
                )}
              </div>
            ))
          ) : (
            phases.slice(0, phaseIdx + 1).map((ph, i) => (
              <div key={ph.id}>
                <div
                  style={{
                    color: i < phaseIdx ? "var(--teal)" : "var(--eb-50)",
                  }}
                >
                  {i < phaseIdx ? "✓" : "▸"} {ph.label}
                </div>
                {ph.steps.map((s, j) => (
                  <div
                    key={`${ph.id}-${j}`}
                    style={{ color: "var(--ink-3)", paddingLeft: 14 }}
                  >
                    {s.done ? "  ✓ " : "    "}
                    {s.label}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 6 deploy-error panel.
//
// Phase 6 (Data Cloud + Tableau Next + sample dashboards) has seven
// non-fatal failure modes that don't flip job.status to "failed" but
// DO leave the demo URL unusable. Each one writes a persisted error
// marker into phase6_outputs so the Monitor can surface it without
// re-deriving the state from individual logs. The banner is amber
// (info, not hard-fail) to match the "Complete (partial)" wording
// the backend emits on line 3830 of pipeline_runner.py.
// ─────────────────────────────────────────────────────────────────────────────

interface Phase6ErrorEntry {
  /** Stable key used for rendering / Copy traceback button. */
  key: string;
  /** Human-readable label shown in the expanded panel. */
  label: string;
  /** Short error message persisted by the worker (80-char trimmed). */
  message: string;
  /** Optional multi-line traceback the worker captured. */
  traceback?: string;
  /**
   * Severity controls per-row styling and the panel's headline copy.
   * Defaults to ``"error"`` for backwards compatibility with the
   * existing rows. ``"warning"`` is used for steps that completed
   * successfully but flagged work the operator must do manually
   * (e.g. engagement DMOs that need Schema Builder UI mapping —
   * pipeline still ran clean, but the dashboards stay broken until
   * the operator opens the UI). Mirrors the split in
   * ``collect_analytics_failures`` (errors only) vs
   * ``engagement_ui_mapping_skipped`` (warnings only) in
   * pipeline_runner.py.
   */
  severity?: "error" | "warning";
}

function collectPhase6Errors(p6: Record<string, unknown> | null | undefined): Phase6ErrorEntry[] {
  if (!p6) return [];
  const getStr = (k: string): string | undefined => {
    const v = p6[k];
    return typeof v === "string" && v ? v : undefined;
  };
  const errors: Phase6ErrorEntry[] = [];

  // PROD-A4 (2026-04-22): outer Phase-6 exception (import error, OOM,
  // KeyError before any sub-step writes its own marker). Without this
  // entry the banner renders empty even though Python flipped
  // partial_success — keep the Python rollup
  // (`collect_analytics_failures`) and this function in lock-step.
  const phase6Err = getStr("phase6_error");
  if (phase6Err) {
    errors.push({
      key: "phase6_error",
      label: "Phase 6 (outer)",
      message: phase6Err,
    });
  }

  // Move 1 (2026-04-25): final readiness gate that runs after every
  // Phase 6 sub-step has reported success. Catches the "deploy reported
  // 200 OK but the DMO doesn't exist on the org" failure shape that hid
  // the missing Contact DPKO for weeks. The structured findings live
  // under p6.phase6_diagnostic (blockers/warnings/full diag summary) for
  // operator drill-down — only the headline string lands in the banner.
  const gateErr = getStr("phase6_diagnostic_gate_error");
  if (gateErr) {
    errors.push({
      key: "phase6_diagnostic_gate_error",
      label: "Phase 6 readiness gate",
      message: gateErr,
    });
  }

  const crmErr = getStr("crm_streams_error");
  if (crmErr) {
    errors.push({ key: "crm_streams_error", label: "CRM kit deploy", message: crmErr });
  }

  // PROD-A3 (2026-04-21): three error keys that the worker now persists
  // but Monitor previously dropped on the floor. crm_kit_activation_error
  // fires when 0/3 kits activated (the cascading-skip case). field_maps
  // and stream_refresh fire when the Connect-API and browser-driven
  // steps fail respectively. Without these the operator saw zero
  // banners on a run that produced empty dashboards (job d380ef1c).
  const crmActErr = getStr("crm_kit_activation_error");
  if (crmActErr) {
    errors.push({
      key: "crm_kit_activation_error",
      label: "CRM kit activation",
      message: crmActErr,
    });
  }

  const fieldMapsErr = getStr("field_maps_error");
  if (fieldMapsErr) {
    errors.push({
      key: "field_maps_error",
      label: "Field maps (Connect API)",
      message: fieldMapsErr,
      traceback: getStr("field_maps_error_traceback"),
    });
  }

  // Move 2 (2026-04-25): currency-field pre-deploy errors. Surfaced as
  // a distinct row above field maps so the operator can tell at a
  // glance whether the failure was upstream (DMO extension never
  // landed → all currency mappings doomed) vs downstream (extension
  // landed, mapping POST itself failed).
  const dmoFieldExtErr = getStr("dmo_field_extension_error");
  if (dmoFieldExtErr) {
    errors.push({
      key: "dmo_field_extension_error",
      label: "DMO field extensions",
      message: dmoFieldExtErr,
      traceback: getStr("dmo_field_extension_traceback"),
    });
  }

  // Move 4 (2026-04-26): engagement DMO UI mapping orchestrator.
  // Two distinct rows on purpose:
  //   * engagement_ui_mapping_error  → real failures (config broken,
  //     harness crashed, real-mode UI step raised). Severity: error.
  //   * engagement_ui_mapping_skipped → expected pre-Stage-2 state
  //     where N>0 DMOs need manual Schema Builder UI work. Severity:
  //     warning. The Python rollup intentionally excludes this from
  //     ``analytics_failures`` (otherwise every fresh deploy would
  //     flip partial_success), so the UI must split the same way to
  //     stay consistent.
  const engagementUiErr = getStr("engagement_ui_mapping_error");
  if (engagementUiErr) {
    errors.push({
      key: "engagement_ui_mapping_error",
      label: "Engagement UI mapping",
      message: engagementUiErr,
      traceback: getStr("engagement_ui_mapping_traceback"),
    });
  }
  const engagementUiSkipped = getStr("engagement_ui_mapping_skipped");
  if (engagementUiSkipped) {
    errors.push({
      key: "engagement_ui_mapping_skipped",
      label: "Engagement DMOs (manual UI work)",
      message: engagementUiSkipped,
      severity: "warning",
    });
  }

  const streamRefreshErr = getStr("stream_refresh_error");
  if (streamRefreshErr) {
    errors.push({
      key: "stream_refresh_error",
      label: "Data stream refresh",
      message: streamRefreshErr,
      traceback: getStr("stream_refresh_error_traceback"),
    });
  }

  // PROD-A4 (2026-04-22): per-stream refresh failures (most common
  // refresh failure mode). Worker writes the PLURAL key with up to 5
  // string entries; collapse them into a single banner row to match
  // the Python rollup, but only when the singular outer-exception key
  // is absent (otherwise we'd double-report).
  const refreshErrsRaw = p6["stream_refresh_errors"];
  if (
    !streamRefreshErr &&
    Array.isArray(refreshErrsRaw) &&
    refreshErrsRaw.length > 0
  ) {
    const first = refreshErrsRaw.find((e): e is string => typeof e === "string" && e.length > 0);
    if (first) {
      errors.push({
        key: "stream_refresh_errors",
        label: "Data stream refresh",
        message: `${refreshErrsRaw.length} streams failed — ${first}`,
      });
    }
  }

  const pbeErr = getStr("pbe_error");
  if (pbeErr) {
    errors.push({
      key: "pbe_error",
      label: "PricebookEntry stream",
      message: pbeErr,
      traceback: getStr("pbe_error_traceback"),
    });
  }

  const siMetaErr = getStr("si_metadata_deploy_error");
  if (siMetaErr) {
    errors.push({
      key: "si_metadata_deploy_error",
      label: "Sales Insights metadata",
      message: siMetaErr,
      traceback: getStr("si_metadata_deploy_error_traceback"),
    });
  }

  const atfErr = getStr("atf_error");
  if (atfErr) {
    errors.push({
      key: "atf_error",
      label: "ATF deploy",
      message: atfErr,
      traceback: getStr("atf_error_traceback"),
    });
  }

  const atfFailed = p6["atf_failed"];
  if (atfFailed && typeof atfFailed === "object") {
    // Friendly labels map cryptic registry keys to UI-visible names so
    // operators don't have to mentally translate
    // ``service_insights_redesign_app`` to "Service Insights".
    const friendlyAppLabel: Record<string, string> = {
      sales_insights_app: "Sales Insights app",
      sales_insights_embedded_app: "Sales Insights Embedded app",
      sales_insights_sdm: "Sales Insights SDM",
      service_insights_app: "Service Insights app",
      service_insights_sdm: "Service Insights SDM",
      service_insights_redesign_app: "Service Insights (Redesign) app",
      service_insights_with_edc_for_tc: "Service Insights app",
      c360_sdm: "C360 Unified SDM",
      c360_metrics: "C360 Metrics",
    };
    for (const [tpl, msg] of Object.entries(atfFailed as Record<string, unknown>)) {
      if (typeof msg !== "string" || !msg) continue;
      const label = friendlyAppLabel[tpl] ?? `ATF template: ${tpl}`;
      errors.push({ key: `atf_failed:${tpl}`, label, message: msg });
    }
  }

  const tnErr = getStr("tableau_next_error");
  if (tnErr) {
    errors.push({ key: "tableau_next_error", label: "Tableau Next deploy", message: tnErr });
  }

  const sampleErr = getStr("sample_dashboard_error");
  if (sampleErr) {
    errors.push({
      key: "sample_dashboard_error",
      label: "Sample dashboards",
      message: sampleErr,
      traceback: getStr("sample_dashboard_error_traceback"),
    });
  }

  return errors;
}

function DeployErrorsPanel({
  rawJob,
}: {
  rawJob: (RawJobShape & { steps_log?: JobStepLog[] }) | null | undefined;
}): JSX.Element | null {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const cfg = (rawJob?.config ?? {}) as Record<string, unknown>;
  const p6 = (cfg["phase6_outputs"] ?? null) as Record<string, unknown> | null;

  const errors = useMemo(() => collectPhase6Errors(p6), [p6]);
  const summary = (p6?.analytics_failure_summary as string[] | undefined) ?? [];

  // 2026-04-29: identify Insights-app failures specifically (Sales
  // Insights, Sales Insights Embedded, Service Insights Redesign,
  // Service Insights with-EDC). The Phase 6 panel previously buried
  // these under a generic "X steps didn't deploy" headline + manual
  // "Show details" expand — operators were closing the page thinking
  // the run was clean even though zero Tableau Next apps had
  // installed. Auto-expand + a second-line callout that names the
  // failed apps verbatim eliminates the "where are my apps?"
  // diagnostic round-trip.
  const failedApps = useMemo(() => {
    return errors
      .filter((e) => e.key.startsWith("atf_failed:"))
      .map((e) => e.key.slice("atf_failed:".length));
  }, [errors]);
  const hasFailedApps = failedApps.length > 0;

  // Auto-expand on first render whenever the panel needs to surface a
  // failed Insights app — the manual "Show details" was getting missed.
  // Operator can still collapse if they want to focus on something else.
  const [expanded, setExpanded] = useState(hasFailedApps);

  if (errors.length === 0) return null;

  // Move 4 (2026-04-26): split errors-only vs warnings-only counts so
  // the headline copy honours the severity distinction. A run that
  // landed clean except for "engagement DMOs need UI work" should NOT
  // greet the operator with "X steps didn't deploy" — that wording
  // reads as a deploy failure.
  const errorCount = errors.filter((e) => (e.severity ?? "error") === "error").length;
  const warningCount = errors.length - errorCount;

  const friendlyAppNames: Record<string, string> = {
    sales_insights_app: "Sales Insights",
    sales_insights_embedded_app: "Sales Insights (Embedded)",
    sales_insights_sdm: "Sales Insights SDM",
    service_insights_app: "Service Insights",
    service_insights_sdm: "Service Insights SDM",
    service_insights_redesign_app: "Service Insights (Redesign)",
    service_insights_with_edc_for_tc: "Service Insights",
    c360_sdm: "C360 Unified SDM",
    c360_metrics: "C360 Metrics",
  };

  const handleCopy = async (entry: Phase6ErrorEntry) => {
    const payload = entry.traceback
      ? `${entry.label}\n${entry.message}\n\n${entry.traceback}`
      : `${entry.label}\n${entry.message}`;
    try {
      await navigator.clipboard.writeText(payload);
      setCopiedKey(entry.key);
      window.setTimeout(() => setCopiedKey(null), 1800);
    } catch {
      // clipboard API may be unavailable in insecure contexts — silent
      // fail is fine, the SE can still read the panel.
    }
  };

  return (
    <div
      className="card"
      style={{
        marginTop: 16,
        padding: "12px 16px",
        borderColor: "var(--orange)",
        background: "rgba(243,154,55,.08)",
        color: "var(--ink)",
        fontSize: 13,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>
            {hasFailedApps && (
              <>⚠️ {failedApps.length === 1 ? "Insights app didn't install" : `${failedApps.length} Insights apps didn't install`}</>
            )}
            {!hasFailedApps && errorCount > 0 && warningCount > 0 && (
              <>⚠️ Data landed, but {errorCount === 1 ? "one" : errorCount} step{errorCount === 1 ? "" : "s"} didn't deploy ({warningCount} more need{warningCount === 1 ? "s" : ""} manual UI work)</>
            )}
            {!hasFailedApps && errorCount > 0 && warningCount === 0 && (
              <>⚠️ Data landed, but {errorCount === 1 ? "one" : errorCount} analytics step{errorCount === 1 ? "" : "s"} didn't deploy</>
            )}
            {!hasFailedApps && errorCount === 0 && warningCount > 0 && (
              <>ℹ️ Data landed — {warningCount === 1 ? "one step" : `${warningCount} steps`} need manual UI work to finish wiring up</>
            )}
          </div>
          {hasFailedApps && (
            <div
              style={{
                marginTop: 4,
                color: "var(--ink-2)",
                fontSize: 12,
                lineHeight: 1.45,
              }}
            >
              <strong>Apps not installed:</strong>{" "}
              {failedApps.map((a) => friendlyAppNames[a] ?? a).join(", ")}
              {errors.length > failedApps.length && (
                <>{" "}· plus {errors.length - failedApps.length} other Phase 6 issue{errors.length - failedApps.length === 1 ? "" : "s"}</>
              )}
            </div>
          )}
          {!hasFailedApps && summary.length > 0 && (
            <div
              style={{
                marginTop: 4,
                color: "var(--ink-3)",
                fontSize: 12,
                lineHeight: 1.45,
              }}
            >
              {summary.slice(0, 2).join(" · ")}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            fontSize: 12,
            padding: "5px 10px",
            borderRadius: 6,
            border: "1px solid var(--bd)",
            background: "white",
            color: "var(--ink)",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {expanded ? "Hide details" : "Show details"}
        </button>
      </div>

      {expanded && (
        <div
          style={{
            marginTop: 12,
            borderTop: "1px solid rgba(243,154,55,.3)",
            paddingTop: 10,
            display: "grid",
            gap: 8,
          }}
        >
          {errors.map((entry) => {
            const isWarning = (entry.severity ?? "error") === "warning";
            return (
            <div
              key={entry.key}
              style={{
                padding: "8px 10px",
                borderRadius: 6,
                background: isWarning ? "rgba(220,235,255,.5)" : "rgba(255,255,255,.6)",
                border: isWarning
                  ? "1px solid rgba(80,140,200,.35)"
                  : "1px solid rgba(243,154,55,.25)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 12, color: "var(--ink)" }}>
                  {entry.label}
                </div>
                {entry.traceback && (
                  <button
                    type="button"
                    onClick={() => { void handleCopy(entry); }}
                    style={{
                      fontSize: 11,
                      padding: "2px 8px",
                      borderRadius: 5,
                      border: "1px solid var(--bd)",
                      background: "white",
                      color: "var(--ink)",
                      cursor: "pointer",
                    }}
                  >
                    {copiedKey === entry.key ? "Copied ✓" : "Copy traceback"}
                  </button>
                )}
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 12,
                  color: "var(--ink-2)",
                  fontFamily:
                    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {entry.message}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
